import { PropsWithChildren, useMemo } from 'react';
import { Button } from '@mui/material';
import { RestError } from '@azure/core-http';
import { addMethodInterceptor, ApiContextProvider } from '../api.context';
import { GroundfloorManagementAPIs } from '@groundfloor-platform/mgmt-service-g-sdk';
import { getEnvironment } from '../../env/environment';
import { useAccessToken } from '../../workos/access-token.hook';
import { useQueryWhoAmI } from '../../queries/directory.queries';
// import { useHideSplashScreenEffect } from '../../../hooks/use-hide-splash-screen.hook';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { Layout } from '../../../app/layout/layout.component';
import { useHideSplashScreenEffect } from '../../../hooks/use-hide-splash-screen.hook';

export const AuthenticatedApi = (props: PropsWithChildren<{}>) => {
  const { getAccessToken } = useAccessToken();

  const api = useMemo(
    () => addMethodInterceptor(new GroundfloorManagementAPIs({ getToken: getAccessToken }, getEnvironment().mgmtUrl)),
    [getAccessToken]
  );

  if (!api) {
    return null;
  }

  return (
    <ApiContextProvider api={api}>
      <UserIdentificationLayer {...props} />
    </ApiContextProvider>
  );
};

function UserIdentificationLayer({ children }: PropsWithChildren<{}>) {
  const whoami = useQueryWhoAmI();
  useHideSplashScreenEffect(!whoami.isLoading);

  if (whoami.isLoading) {
    return null;
  }

  if (whoami.isError) {
    // 401 means no account; anything else (e.g. server unreachable) isn't an invite problem.
    if ((whoami.error as RestError)?.statusCode === 401) {
      return (
        <Layout>
          <ErrorFullPage
            error={{
              title: 'Invitation Required',
              message:
                'You have successfully signed in, but you need to be invited to access this application. If you believe this is an error, please contact your administrator.'
            }}
          />
        </Layout>
      );
    }

    return (
      <Layout>
        <ErrorFullPage
          error={{
            title: 'Unable to connect',
            message: 'We couldn’t process your request. Please check your connection and try again.'
          }}
        />
        <Button variant="contained" sx={{ mt: 3 }} onClick={() => whoami.refetch()}>
          Try again
        </Button>
      </Layout>
    );
  }

  return <>{children}</>;
}
